import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Loader2, CheckCircle2, Copy, CreditCard, Zap } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const brl = (v) => `R$ ${Number(v || 0).toFixed(2)}`;

export default function FaturaPublicaPage() {
  const { faturaId } = useParams();
  const [fatura, setFatura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [pagando, setPagando] = useState(false);
  const [pix, setPix] = useState(null);
  const [aprovado, setAprovado] = useState(false);
  const [msgConfirmado, setMsgConfirmado] = useState('');

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/fatura-publica/${faturaId}`);
      setFatura(r.data);
      document.title = r.data.loja_nome || 'Pagamento';
      if (r.data.status === 'approved') { setAprovado(true); setMsgConfirmado(r.data.msg_pagamento_confirmado || ''); }
    } catch (e) { setErro(e.response?.data?.detail || 'Fatura não encontrada'); }
    finally { setLoading(false); }
  }, [faturaId]);

  useEffect(() => { carregar(); }, [carregar]);

  // polling do status enquanto não aprovado
  useEffect(() => {
    if (aprovado || !fatura) return;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/fatura-publica/${faturaId}/status`);
        if (r.data.status === 'approved') { setAprovado(true); setMsgConfirmado(r.data.msg_pagamento_confirmado || ''); clearInterval(iv); }
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [aprovado, fatura, faturaId]);

  const pagar = async (metodo) => {
    setPagando(true);
    try {
      const r = await axios.post(`${API}/fatura-publica/${faturaId}/pagar`, { metodo });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      setPix(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar pagamento'); }
    setPagando(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-950"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>;
  if (erro) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white text-center px-4"><p>{erro}</p></div>;

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center px-4 py-8">
      <Toaster position="top-center" />
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <p className="text-xs text-white/40 text-center">{fatura.loja_nome}</p>

          {aprovado ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-3" />
              <h1 className="text-xl font-bold">Pagamento confirmado!</h1>
              <p className="text-2xl font-bold text-emerald-500 mt-2">{brl(fatura.valor)}</p>
              {msgConfirmado && <p className="text-sm text-white/70 mt-3 whitespace-pre-wrap">{msgConfirmado}</p>}
            </div>
          ) : (
            <>
              <div className="text-center mt-2 mb-5">
                <p className="text-sm text-white/60">Olá, {fatura.nome}</p>
                <p className="text-3xl font-bold mt-1">{brl(fatura.valor)}</p>
                {fatura.descricao && <p className="text-sm text-white/50 mt-1">{fatura.descricao}</p>}
              </div>

              {!fatura.aceita_pagamento ? (
                <p className="text-center text-amber-400 text-sm">Pagamento indisponível no momento.</p>
              ) : pix ? (
                <div className="text-center">
                  {pix.qr_code && <img src={pix.qr_code.startsWith('data:') ? pix.qr_code : `data:image/png;base64,${pix.qr_code}`} alt="QR PIX" className="w-56 h-56 mx-auto rounded-lg bg-white p-2" />}
                  {pix.qr_code_text && (
                    <button onClick={() => { navigator.clipboard.writeText(pix.qr_code_text); toast.success('Código PIX copiado!'); }}
                      className="mt-4 w-full py-3 rounded-xl bg-white/10 text-sm font-medium flex items-center justify-center gap-2"><Copy className="w-4 h-4" /> Copiar código PIX</button>
                  )}
                  <p className="text-xs text-white/40 mt-3">A tela atualiza sozinha quando o pagamento cair.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <button onClick={() => pagar('pix')} disabled={pagando} className="w-full py-3.5 rounded-xl bg-emerald-500 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                    {pagando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-5 h-5" />} Pagar com PIX
                  </button>
                  {fatura.suporta_cartao && (
                    <button onClick={() => pagar('cartao')} disabled={pagando} className="w-full py-3.5 rounded-xl border border-white/20 font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                      <CreditCard className="w-5 h-5" /> Pagar com cartão
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <p className="text-center text-[11px] text-white/25 mt-4">Pagamento seguro</p>
      </div>
    </div>
  );
}
